import type { AutomationProjectDto } from '@tms/shared';
import { useNavigate } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useT } from '../../../i18n';
import {
  useClasses,
  useDeleteProject,
  useProjectRuns,
  useSuites,
  useTestData,
  useUpdateProject,
} from '../hooks';
import {
  DangerZone,
  ProjectForm,
  projectDraftValid,
  useFailToast,
  type ProjectDraft,
} from './shared';

/** What deleting the project takes along, counted from what the project pages load anyway. */
const ProjectImpact = ({ projectId }: { projectId: string }) => {
  const { t } = useT();
  const suites = useSuites(projectId);
  const classes = useClasses(projectId);
  const testData = useTestData(projectId);
  const runs = useProjectRuns(projectId);
  if (!suites.data || !classes.data || !testData.data || !runs.data) {
    return <p>{t('automation.project.danger.counting')}</p>;
  }
  return (
    <p>
      {t('automation.project.settings.impact', {
        suites: suites.data.length,
        classes: classes.data.length,
        data: testData.data.length,
        runs: runs.data.length,
      })}
    </p>
  );
};

/** A project's settings and deleting it. Admins only. */
export const ProjectSettingsDialog = ({
  project,
  onOpenChange,
}: {
  project: AutomationProjectDto;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const navigate = useNavigate();
  const failToast = useFailToast();
  const update = useUpdateProject(project.id);
  const remove = useDeleteProject(project.id);
  // mounted when opened, so the form starts from the project as it is now
  const [draft, setDraft] = useState<ProjectDraft>({
    name: project.name,
    description: project.description,
    note: project.note ?? '',
    gitlabProjectId: project.gitlabProjectId,
    gitlabPipelineUrl: project.gitlabPipelineUrl,
    jenkinsUrl: project.jenkinsUrl ?? '',
    testType: project.testType,
  });
  // write-only: empty keeps the token, '' with «remove» drops it
  const [triggerToken, setTriggerToken] = useState('');
  const [removeToken, setRemoveToken] = useState(false);

  const save = () =>
    update.mutate(
      {
        name: draft.name.trim(),
        description: draft.description,
        note: draft.note.trim(),
        gitlabProjectId: draft.gitlabProjectId.trim(),
        gitlabPipelineUrl: draft.gitlabPipelineUrl.trim(),
        // empty clears it — the old TestRun form could not
        jenkinsUrl: draft.jenkinsUrl.trim() || null,
        testType: draft.testType,
        ...(removeToken
          ? { triggerToken: '' }
          : triggerToken.trim()
            ? { triggerToken: triggerToken.trim() }
            : {}),
      },
      {
        onSuccess: () => {
          toast.success(t('automation.project.settings.saved'));
          onOpenChange(false);
        },
        onError: failToast,
      },
    );

  const doDelete = () =>
    remove.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('automation.project.settings.deleted', { name: project.name }));
        onOpenChange(false);
        void navigate({ to: '/automation/teams/$teamId', params: { teamId: project.teamId } });
      },
      onError: failToast,
    });

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={t('automation.project.settings.title')}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <ProjectForm
          draft={draft}
          withHints
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        >
          <Field
            label={t('automation.project.field.triggerToken')}
            hint={
              removeToken
                ? t('automation.project.field.triggerTokenRemoved')
                : project.hasTriggerToken
                  ? t('automation.project.field.triggerTokenSet')
                  : t('automation.project.field.triggerTokenUnset')
            }
          >
            {({ id, describedBy }) => (
              <div className="flex items-center gap-2">
                <Input
                  id={id}
                  type="password"
                  autoComplete="off"
                  aria-describedby={describedBy}
                  value={triggerToken}
                  disabled={removeToken}
                  onChange={(event) => setTriggerToken(event.target.value)}
                />
                {project.hasTriggerToken ? (
                  <label className="flex shrink-0 items-center gap-1.5 text-[13px] text-ink-muted">
                    <input
                      type="checkbox"
                      checked={removeToken}
                      onChange={(event) => setRemoveToken(event.target.checked)}
                      className="size-4 rounded border-border"
                    />
                    {t('automation.project.field.triggerTokenRemove')}
                  </label>
                ) : null}
              </div>
            )}
          </Field>
        </ProjectForm>

        <div className="flex justify-end gap-2">
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!projectDraftValid(draft)}
            loading={update.isPending}
            onClick={save}
          >
            <Save className="size-3.5" />
            {t('common.save')}
          </Button>
        </div>

        <DangerZone
          title={t('automation.project.settings.dangerTitle')}
          text={t('automation.project.settings.dangerText')}
          deleteLabel={t('automation.project.settings.delete')}
          name={project.name}
          impact={<ProjectImpact projectId={project.id} />}
          loading={remove.isPending}
          onDelete={doDelete}
        />
      </div>
    </Dialog>
  );
};
